const MembershipPlan = require('@/lib/Memberships/MembershipPlan');
const SubscriptionPayment = require('@/lib/Memberships/SubscriptionPayment');
const { populate } = require('@/functions/objects');
const { toDate, toEndOfDayDate, toInt, toNullableBool, toNullableFloat } = require('@/functions/transform');

/**
 * @param {any} data
 *
 * @constructor
 */
const MembershipSubscription = function (data = {}) {
    /**
     * @type {?number}
     */
    this.ID = null;

    /**
     * @type {Date|null}
     */
    this.startDate = null;

    /**
     * @type {Date|null}
     */
    this.endDate = null;

    /**
     * @type {Date|null}
     */
    this.nextPaymentDueAt = null;

    /**
     * @type {float|null}
     */
    this.installmentAmount = null;

    /**
     * Is this the current/active subscription for the associated membership?
     *
     * @type {boolean|null}
     */
    this.isCurrent = null;

    /**
     * Used when changing an installment amount.
     *
     * @type {float|null}
     */
    this.newInstallmentAmount = null;

    /**
     * @type {number|null}
     */
    this.itemInstanceID = null;

    /**
     * @type {number}
     */
    this.maxRedemptions = 0;

    /**
     * @type {number|null}
     */
    this.membershipID = null;

    /**
     * @type {MembershipPlan|null}
     */
    this.membershipPlan = null;

    /**
     * @type {number}
     */
    this.visitCount = 0;

    /**
     * @type {SubscriptionPayment[]}
     */
    this.subscriptionPayments = [];

    populate(this, data);
};

MembershipSubscription.prototype = {
    getHydrationMap() {
        return {
            endDate: toEndOfDayDate,
            ID: {
                field: ['ID', 'membershipSubscriptionID'],
                transform: toInt,
            },
            installmentAmount: toNullableFloat,
            isCurrent: toNullableBool,
            itemInstanceID: toInt,
            maxRedemptions: toInt,
            membershipID: toInt,
            membershipPlan: {
                field: ['membershipPlan', 'membershipplans'],
                model: MembershipPlan,
            },
            nextPaymentDueAt: toDate,
            startDate: toDate,
            subscriptionPayments: {
                modelCollection: SubscriptionPayment,
            },
            visitCount: toInt,
        };
    },

    /**
     * @return {boolean}
     */
    hasInstallments() {
        return this.installmentAmount > 0;
    },

    /**
     * @return {number}
     */
    getVisits() {
        return parseInt(this.visitCount, 10);
    },

    /**
     * @param {number} newVisitCount
     */
    setVisits(newVisitCount) {
        this.visitCount = newVisitCount;
    },

    /**
     * Method to check whether the number of logged visits is currently at, or will
     * be at, the maximum allowed.
     * If no value is passed in, the current number of visits is assumed.
     * A max redemptions value of zero is special - it means unlimited.
     *
     * @param {?number} [newVisitCount]
     *
     * @return {boolean}
     */
    isAtMaximumVisits(newVisitCount) {
        if (this.maxRedemptions === 0) {
            return false;
        }

        if (newVisitCount === undefined) {
            // Is the current number of visits at or over the maximum allowed?
            return (this.visitCount >= this.maxRedemptions);
        }

        // Will the new number of visits exceed the maximum allowed?
        return (newVisitCount > this.maxRedemptions);
    },

    isActive() {
        let now = new Date();
        // Membership subscriptions have start and end *dates*, which don't include a time component.
        now.setHours(0, 0, 0, 0);
        return this.startDate <= now && this.endDate >= now;
    },
};

module.exports = MembershipSubscription;
